import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import Navbar from "../Navbar";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { jsPDF } from "jspdf";
import {
  FaPlus,
  FaTrash,
  FaFileInvoiceDollar,
  FaUserTie,
  FaTable,
  FaUniversity,
} from "react-icons/fa";
import "../../../App.css";

const COMPANY = {
  name: "ARVANCH SOFTWARE SOLUTIONS PRIVATE LIMITED",
  address:
    "104 FLOOR 1ST PLOT 69A, PANDIT MM MALVIYA MARG,\nTulsiwadi, Mumbai, Mumbai- 400034, Maharashtra",
  logoPath: "/imgs/arvanch-logo.png",
  phone: "+91-91214 15891",
  email: "contact@grademe-ai.com"
};

const BANK = {
  holderName: "ARVANCH SOFTWARE SOLUTIONS PRIVATE LTD",
  accountNumber: "5020 0115 3819 20",
  ifsc: "HDFC0000501",
  bank: "HDFC Bank",
  branch: "CHURCHGATE INDUSTRY HOUSE",
};

const InvoiceGenerator = () => {
  // ── Recipient State ──
  const [recipient, setRecipient] = useState({
    name: "",
    institute: "",
    address: "",
    email: "",
    phone: "",
  });

  // ── Document Meta ──
  const [documentType, setDocumentType] = useState("invoice"); // "invoice" or "receipt"
  const todayISO = new Date().toISOString().split("T")[0];
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState(todayISO);
  
  // ── Receipt Meta ──
  const [previousPaymentAmount, setPreviousPaymentAmount] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");

  // ── Line Items ──
  const [rows, setRows] = useState([
    { id: Date.now(), qty: "", description: "", unitPrice: "" },
  ]);
  const [animatingRowId, setAnimatingRowId] = useState(null);

  // ── Discount ──
  const [discount, setDiscount] = useState("");

  // ── PDF State ──
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const logoRef = useRef(null);

  // Set page title and meta tags (replaces react-helmet)
    useEffect(() => {
      document.title = "Invoice Generator \u2014 Arvanch (Internal)";
      const setMeta = (name, content) => {
        let el = document.querySelector(`meta[name="${name}"]`);
        if (!el) { el = document.createElement("meta"); el.name = name; document.head.appendChild(el); }
        el.content = content;
      };
      setMeta("robots", "noindex, nofollow, noarchive, nosnippet");
      setMeta("googlebot", "noindex, nofollow");
    }, []);

  // Preload logo
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = COMPANY.logoPath;
    img.onload = () => {
      logoRef.current = img;
    };
  }, []);

  useEffect(() => {
      document.title = "Invoice Generator - Arvanch";
      const metaRobots = document.createElement("meta");
      metaRobots.name = "robots";
      metaRobots.content = "noindex, nofollow, noarchive, nosnippet";
      const metaGooglebot = document.createElement("meta");
      metaGooglebot.name = "googlebot";
      metaGooglebot.content = "noindex, nofollow";
  
      document.head.appendChild(metaRobots);
      document.head.appendChild(metaGooglebot);
  
      return () => {
        document.head.removeChild(metaRobots);
        document.head.removeChild(metaGooglebot);
      };
    }, []);

  // ── Row Helpers ──
  const addRow = () => {
    const newRow = { id: Date.now(), qty: "", description: "", unitPrice: "" };
    setRows((prev) => [...prev, newRow]);
    setAnimatingRowId(newRow.id);
    setTimeout(() => setAnimatingRowId(null), 500);
  };

  const removeRow = (id) => {
    if (rows.length === 1) return;
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const updateRow = (id, field, value) => {
    setRows((prev) =>
      prev.map((r) => (r.id === id ? { ...r, [field]: value } : r))
    );
  };

  // ── Calculations ──
  const rowAmount = useCallback((row) => {
    const qty = parseFloat(row.qty) || 0;
    const price = parseFloat(row.unitPrice) || 0;
    return qty * price;
  }, []);

  const subtotal = useMemo(() => {
    return rows.reduce((sum, r) => sum + rowAmount(r), 0);
  }, [rows, rowAmount]);

  const discountPercent = useMemo(() => {
    const val = parseFloat(discount) || 0;
    return Math.min(Math.max(val, 0), 100);
  }, [discount]);

  const discountAmount = useMemo(
    () => subtotal * (discountPercent / 100),
    [subtotal, discountPercent]
  );

  const totalDue = useMemo(
    () => subtotal - discountAmount,
    [subtotal, discountAmount]
  );

  const balanceAmount = useMemo(() => {
    const prev = parseFloat(previousPaymentAmount) || 0;
    const curr = parseFloat(paymentAmount) || 0;
    const calculated = totalDue - (prev + curr);
    return calculated > 0 ? calculated : 0;
  }, [totalDue, previousPaymentAmount, paymentAmount]);

  // ── Format ──
  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

  const formatDateIndian = (isoDate) => {
    if (!isoDate) return "";
    const [y, m, d] = isoDate.split("-");
    return `${d}/${m}/${y}`;
  };

  // ── Number to Words (Indian) ──
  const numberToWordsIndian = (num) => {
    if (num === 0) return "Zero";
    const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
      "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

    const numToWords = (n) => {
      if (n === 0) return "";
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : "");
      if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " and " + numToWords(n % 100) : "");
      if (n < 100000) return numToWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + numToWords(n % 1000) : "");
      if (n < 10000000) return numToWords(Math.floor(n / 100000)) + " Lakh" + (n % 100000 ? " " + numToWords(n % 100000) : "");
      return numToWords(Math.floor(n / 10000000)) + " Crore" + (n % 10000000 ? " " + numToWords(n % 10000000) : "");
    };

    const wholePart = Math.floor(Math.abs(num));
    const decimalPart = Math.round((Math.abs(num) - wholePart) * 100);
    let result = numToWords(wholePart) + " Rupees";
    if (decimalPart > 0) {
      result += " and " + numToWords(decimalPart) + " Paise";
    }
    result += " Only";
    return result;
  };


  // ── PDF Generation ──
  const handleGeneratePdf = async () => {
    setIsGeneratingPdf(true);
    try {
      const pdf = new jsPDF("p", "pt", "a4");

      // Load Roboto fonts for ₹ symbol
      const fetchFont = async (url, vfsName, fontName, fontStyle) => {
        const resp = await fetch(url);
        const buffer = await resp.arrayBuffer();
        let binary = "";
        const bytes = new Uint8Array(buffer);
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        const base64 = window.btoa(binary);
        pdf.addFileToVFS(vfsName, base64);
        pdf.addFont(vfsName, fontName, fontStyle);
      };

      await fetchFont(
        "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf",
        "Roboto-Regular.ttf",
        "Roboto",
        "normal"
      );
      await fetchFont(
        "https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf",
        "Roboto-Medium.ttf",
        "Roboto",
        "bold"
      );

      const W = pdf.internal.pageSize.getWidth(); // 595.28
      const H = pdf.internal.pageSize.getHeight(); // 841.89
      const M = 40; // margin
      const cW = W - M * 2;
      let y = 0;

      // ═══════════════════════════════════════════════
      // TOP BRAND BAR
      // ═══════════════════════════════════════════════
      pdf.setFillColor(171, 208, 55); // --color-6 green
      pdf.rect(0, 0, W, 5, "F");

      // ═══════════════════════════════════════════════
      // HEADER: Logo + Company Name + INVOICE title
      // ═══════════════════════════════════════════════
      y = 28;
      if (logoRef.current) {
        pdf.addImage(logoRef.current, "PNG", M, y - 6, 32, 32);
      }
      const logoOffset = logoRef.current ? 40 : 0;

      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(14);
      pdf.setTextColor(171, 208, 55);
      pdf.text("ARVANCH", M + logoOffset, y + 10);

      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(7);
      pdf.setTextColor(100, 100, 100);
      pdf.text("SOFTWARE SOLUTIONS PRIVATE LIMITED", M + logoOffset, y + 22);

      // INVOICE title
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(26);
      pdf.setTextColor(171, 208, 55);
      pdf.text(documentType === "receipt" ? "PAYMENT RECEIPT" : "INVOICE", W - M, y + 14, { align: "right" });

      // ═══════════════════════════════════════════════
      // DIVIDER
      // ═══════════════════════════════════════════════
      y += 34;
      pdf.setDrawColor(171, 208, 55);
      pdf.setLineWidth(1.5);
      pdf.line(M, y, W - M, y);

      // ═══════════════════════════════════════════════
      // INVOICE # & DATE ROW
      // ═══════════════════════════════════════════════
      y += 16;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(120, 120, 120);
      pdf.text(documentType === "receipt" ? "RECEIPT NO." : "INVOICE NO.", M, y);
      pdf.text("DATE", W - M - 80, y);

      y += 13;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(10);
      pdf.setTextColor(40, 40, 40);
      pdf.text(invoiceNumber || "—", M, y);
      pdf.text(formatDateIndian(invoiceDate), W - M - 80, y);

      // ═══════════════════════════════════════════════
      // FROM / BILL TO
      // ═══════════════════════════════════════════════
      y += 26;
      const colFromX = M;
      const colToX = W / 2 + 20;

      // FROM
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(171, 208, 55);
      pdf.text("FROM", colFromX, y);

      let fy = y + 14;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(40, 40, 40);
      pdf.text("Arvanch Software Solutions Pvt. Ltd.", colFromX, fy);

      fy += 13;
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(90, 90, 90);
      const companyAddr = COMPANY.address.split("\n");
      companyAddr.forEach((line) => {
        pdf.text(line, colFromX, fy);
        fy += 11;
      });

      
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(90, 90, 90);
      pdf.text("Email: " + COMPANY.email, colFromX, fy);
      fy += 11;
      pdf.text("Phone: " + COMPANY.phone, colFromX, fy);

      // BILL TO
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(171, 208, 55);
      pdf.text("BILL TO", colToX, y);

      let ty = y + 14;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(9.5);
      pdf.setTextColor(40, 40, 40);
      const nameLines = pdf.splitTextToSize(recipient.name || "—", cW / 2 - 30);
      nameLines.forEach((line) => {
        pdf.text(line, colToX, ty);
        ty += 12;
      });

      ty += 1;
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(8);
      pdf.setTextColor(90, 90, 90);
      if (recipient.institute && recipient.institute.trim().toLowerCase() !== (recipient.name || "").trim().toLowerCase()) {
        pdf.text(recipient.institute, colToX, ty);
        ty += 11;
      }
      if (recipient.address) {
        const addrLines = pdf.splitTextToSize(recipient.address, cW / 2 - 30);
        addrLines.forEach((line) => {
          pdf.text(line, colToX, ty);
          ty += 11;
        });
      }
      if (recipient.email) {
        pdf.text(`Email: ${recipient.email}`, colToX, ty);
        ty += 11;
      }
      if (recipient.phone) {
        pdf.text(`Phone: +${recipient.phone}`, colToX, ty);
        ty += 11;
      }

      // ═══════════════════════════════════════════════
      // TABLE
      // ═══════════════════════════════════════════════
      y = Math.max(fy, ty) + 16;

      const cols = [
        { label: "#", w: 24, align: "center" },
        { label: "Description", w: 297.28, align: "left" },
        { label: "Qty", w: 34, align: "center" },
        { label: "Unit Price (₹)", w: 80, align: "right" },
        { label: "Amount (₹)", w: 80, align: "right" },
      ];
      const totalTableW = cols.reduce((s, c) => s + c.w, 0); // 515.28 = cW
      const tableOffsetX = M;

      // Table header
      pdf.setFillColor(171, 208, 55);
      pdf.roundedRect(tableOffsetX, y - 4, totalTableW, 22, 3, 3, "F");
      pdf.setFontSize(7.5);
      pdf.setFont("Roboto", "bold");
      pdf.setTextColor(255, 255, 255);

      let cx = tableOffsetX;
      cols.forEach((col) => {
        const tx =
          col.align === "right"
            ? cx + col.w - 8
            : col.align === "center"
              ? cx + col.w / 2
              : cx + 8;
        pdf.text(col.label, tx, y + 10, {
          align: col.align === "left" ? "left" : col.align,
        });
        cx += col.w;
      });

      // Table rows with dynamic word-wrap
      y += 22;
      const validRows = rows.filter(
        (r) =>
          r.description || parseFloat(r.qty) > 0 || parseFloat(r.unitPrice) > 0
      );
      const dataRows = validRows.length > 0 ? validRows : rows;

      let curY = y;
      const lineHeight = 11;

      dataRows.forEach((row, idx) => {
        const amt = rowAmount(row);

        pdf.setFontSize(8);
        pdf.setFont("Roboto", "normal");
        pdf.setTextColor(55, 55, 55);

        // Word wrap description to available column width
        const descWidth = cols[1].w - 16;
        const rawDesc = (row.description || "—").trim() || "—";
        const descLines = pdf.splitTextToSize(rawDesc, descWidth);
        const lineCount = Math.max(1, descLines.length);
        const rowHeight = 22 + (lineCount - 1) * lineHeight;

        // Alternating row background
        if (idx % 2 === 0) {
          pdf.setFillColor(248, 250, 248);
          pdf.rect(tableOffsetX, curY - 2, totalTableW, rowHeight, "F");
        }

        // Subtle row divider line between rows
        if (idx > 0) {
          pdf.setDrawColor(240, 243, 240);
          pdf.setLineWidth(0.5);
          pdf.line(tableOffsetX, curY - 2, tableOffsetX + totalTableW, curY - 2);
        }

        let rx = tableOffsetX;

        // Col 0: #
        pdf.text(String(idx + 1), rx + cols[0].w / 2, curY + 12, { align: "center" });
        rx += cols[0].w;

        // Col 1: Description (multi-line word-wrapped)
        descLines.forEach((dLine, dIdx) => {
          pdf.text(dLine, rx + 8, curY + 12 + dIdx * lineHeight, { align: "left" });
        });
        rx += cols[1].w;

        // Col 2: Qty
        pdf.text(String(parseFloat(row.qty) || 0), rx + cols[2].w / 2, curY + 12, { align: "center" });
        rx += cols[2].w;

        // Col 3: Unit Price
        pdf.text(
          formatCurrency(parseFloat(row.unitPrice) || 0).replace("₹", "₹ "),
          rx + cols[3].w - 8,
          curY + 12,
          { align: "right" }
        );
        rx += cols[3].w;

        // Col 4: Amount
        pdf.text(
          formatCurrency(amt).replace("₹", "₹ "),
          rx + cols[4].w - 8,
          curY + 12,
          { align: "right" }
        );

        curY += rowHeight;
      });

      y = curY;

      // Bottom line of table
      pdf.setDrawColor(200, 210, 200);
      pdf.setLineWidth(0.5);
      pdf.line(tableOffsetX, y - 2, tableOffsetX + totalTableW, y - 2);

      // ═══════════════════════════════════════════════
      // SUMMARY (right-aligned)
      // ═══════════════════════════════════════════════
      y += 16;
      const summaryEndX = tableOffsetX + totalTableW - 8;
      const summaryX = summaryEndX - 220;

      // Subtotal
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(9);
      pdf.setTextColor(100, 100, 100);
      pdf.text("Subtotal", summaryX, y);
      pdf.setFont("Roboto", "bold");
      pdf.setTextColor(50, 50, 50);
      pdf.text(formatCurrency(subtotal), summaryEndX, y, { align: "right" });
      y += 16;

      // Discount (only if > 0)
      if (discountPercent > 0) {
        pdf.setFont("Roboto", "normal");
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Discount (${discountPercent}%)`, summaryX, y);
        pdf.setFont("Roboto", "bold");
        pdf.setTextColor(220, 50, 50);
        pdf.text(`- ${formatCurrency(discountAmount)}`, summaryEndX, y, {
          align: "right",
        });
        y += 16;
      }

      // Thin line
      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.5);
      pdf.line(summaryX, y, summaryEndX, y);
      y += 14;

      // TOTAL AMOUNT DUE OR RECEIPT
      pdf.setFillColor(171, 208, 55);
      const hasPrevPayment = parseFloat(previousPaymentAmount) > 0;
      const boxHeight = documentType === "receipt" ? (hasPrevPayment ? 70 : 54) : 30;
      pdf.roundedRect(summaryX - 8, y - 12, 236, boxHeight, 4, 4, "F");
      pdf.setFontSize(9.5);
      pdf.setFont("Roboto", "bold");
      pdf.setTextColor(255, 255, 255);
      
      if (documentType === "invoice") {
        pdf.text("Total Amount Due", summaryX, y + 6);
        pdf.setFontSize(13);
        pdf.text(formatCurrency(totalDue), summaryEndX, y + 6, {
          align: "right",
        });
      } else {
        let textY = y + 2;
        pdf.text("Bill Amount", summaryX, textY);
        pdf.text(formatCurrency(totalDue), summaryEndX, textY, { align: "right" });
        textY += 16;
        
        if (hasPrevPayment) {
          pdf.text("Previous Payment", summaryX, textY);
          pdf.text(formatCurrency(parseFloat(previousPaymentAmount)), summaryEndX, textY, { align: "right" });
          textY += 16;
        }
        
        pdf.text("Current Payment", summaryX, textY);
        pdf.text(formatCurrency(parseFloat(paymentAmount) || 0), summaryEndX, textY, { align: "right" });
        textY += 16;
        
        pdf.text("Balance Amount", summaryX, textY);
        pdf.text(formatCurrency(balanceAmount), summaryEndX, textY, { align: "right" });
      }

      // ── Amount in Words ──
      y += boxHeight + 6;
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(7.5);
      pdf.setTextColor(100, 100, 100);
      pdf.text(documentType === "receipt" ? "Payment Received in Words:" : "Amount in Words:", M, y);
      y += 12;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(60, 60, 60);
      const amtForWords = documentType === "receipt" ? (parseFloat(paymentAmount) || 0) : totalDue;
      const amtWords = numberToWordsIndian(Math.round(amtForWords));
      const wordLines = pdf.splitTextToSize(amtWords, cW);
      wordLines.forEach((line) => {
        pdf.text(line, M, y);
        y += 11;
      });

      // ═══════════════════════════════════════════════
      // BANK DETAILS
      // ═══════════════════════════════════════════════
      y += 10;
      pdf.setDrawColor(230, 230, 230);
      pdf.setLineWidth(0.5);
      pdf.line(M, y, W - M, y);

      y += 16;
      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(8);
      pdf.setTextColor(171, 208, 55);
      pdf.text("BANK DETAILS FOR PAYMENT", M, y);

      y += 14;
      pdf.setFontSize(8);
      pdf.setTextColor(70, 70, 70);

      const bankDetails = [
        ["Account Holder Name", BANK.holderName],
        ["Account Number", BANK.accountNumber],
        ["IFSC Code", BANK.ifsc],
        ["Bank", BANK.bank],
        ["Branch", BANK.branch],
      ];

      bankDetails.forEach(([label, value]) => {
        pdf.setFont("Roboto", "normal");
        pdf.setTextColor(110, 110, 110);
        pdf.text(`${label}:`, M, y);
        pdf.setFont("Roboto", "bold");
        pdf.setTextColor(50, 50, 50);
        pdf.text(value, M + 120, y);
        y += 13;
      });

      // ═══════════════════════════════════════════════
      // NOTES / TERMS
      // ═══════════════════════════════════════════════
      y += 8;
      pdf.setDrawColor(230, 230, 230);
      pdf.setLineWidth(0.5);
      pdf.line(M, y, W - M, y);
      y += 14;

      pdf.setFont("Roboto", "bold");
      pdf.setFontSize(7);
      pdf.setTextColor(150, 150, 150);
      pdf.text("TERMS & CONDITIONS", M, y);
      y += 12;
      pdf.setFont("Roboto", "normal");
      pdf.setFontSize(6.5);
      pdf.setTextColor(150, 150, 150);
      const terms = [
        "1. Payment is due within 15 days from the date of invoice.",
        "2. Please transfer the amount to the bank details mentioned above.",
        "3. This is a computer-generated invoice and does not require a physical signature.",
      ];
      terms.forEach((t) => {
        pdf.text(t, M, y);
        y += 10;
      });

      // ═══════════════════════════════════════════════
      // FOOTER
      // ═══════════════════════════════════════════════
      const footerY = H - 28;
      pdf.setDrawColor(171, 208, 55);
      pdf.setLineWidth(1);
      pdf.line(M, footerY - 10, W - M, footerY - 10);
      pdf.setFontSize(6.5);
      pdf.setTextColor(160, 160, 160);
      pdf.setFont("Roboto", "normal");
      pdf.text(
        "Arvanch Software Solutions Pvt. Ltd.  •  www.arvanch.com  •  Mumbai, Maharashtra",
        W / 2,
        footerY,
        { align: "center" }
      );

      // Bottom brand bar
      pdf.setFillColor(171, 208, 55);
      pdf.rect(0, H - 5, W, 5, "F");

      // ── SAVE ──
      const safeName = (recipient.name || "Client").replace(/[^a-zA-Z0-9]/g, "_");
      const prefix = documentType === "receipt" ? "Receipt" : "Invoice";
      pdf.save(`${prefix}_${invoiceNumber || "DRAFT"}_${safeName}.pdf`);
    } catch (err) {
      console.error("Failed to generate invoice PDF", err);
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  // ── Breadcrumbs ──
  const breadcrumbs = [
    { name: "Admin", path: "/admin", id: "admin" },
    {
      name: "Invoice Generator",
      path: "/admin/invoice-generator",
      id: "invoice-generator",
      isLast: true,
    },
  ];

  return (
    <>
      <Navbar breadcrumbs={breadcrumbs} />

      <div className="inv-page">
        <div className="inv-container">
          {/* ══════════ HEADER ══════════ */}
          <div className="inv-header">
            <div className="inv-header-left">
              <img
                src={COMPANY.logoPath}
                alt="Arvanch Logo"
                className="inv-company-logo"
              />
              <div>
                <h1 className="inv-company-name">ARVANCH</h1>
                <p className="inv-company-sub">
                  Software Solutions Private Limited
                </p>
              </div>
            </div>
            <div className="inv-header-right">
              <h2 className="inv-title">Invoice Generator</h2>
              <p className="inv-subtitle">Create professional invoices</p>
            </div>
          </div>

          {/* ══════════ FORM SECTIONS ══════════ */}
          <div className="inv-form-sections">
            {/* ── Bill To ── */}
            <div className="inv-section">
              <div className="inv-section-title">
                <FaUserTie className="inv-section-icon" />
                Bill To
              </div>
              <div className="inv-form-grid">
                <div className="inv-form-group">
                  <label>Recipient Name</label>
                  <input
                    type="text"
                    placeholder="e.g. John Doe"
                    value={recipient.name}
                    onChange={(e) =>
                      setRecipient({ ...recipient, name: e.target.value })
                    }
                  />
                </div>
                <div className="inv-form-group">
                  <label>Institute / Company Name</label>
                  <input
                    type="text"
                    placeholder="e.g. ABC Technologies Pvt. Ltd."
                    value={recipient.institute}
                    onChange={(e) =>
                      setRecipient({ ...recipient, institute: e.target.value })
                    }
                  />
                </div>
                <div className="inv-form-group inv-full-width">
                  <label>Address</label>
                  <textarea
                    rows={2}
                    placeholder="Full billing address..."
                    value={recipient.address}
                    onChange={(e) =>
                      setRecipient({ ...recipient, address: e.target.value })
                    }
                  />
                </div>
                <div className="inv-form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="e.g. john@company.com"
                    value={recipient.email}
                    onChange={(e) =>
                      setRecipient({ ...recipient, email: e.target.value })
                    }
                  />
                </div>
                <div className="inv-form-group">
                  <label>Phone Number</label>
                  <PhoneInput
                    country={"in"}
                    value={recipient.phone}
                    onChange={(phone, data) => {
                      let cleanValue = phone || "";
                      if (data && data.dialCode) {
                        if (cleanValue.startsWith(data.dialCode)) {
                          let nationalNumber = cleanValue.slice(data.dialCode.length);
                          cleanValue = data.dialCode + nationalNumber.replace(/^0+/, "");
                        } else {
                          cleanValue = data.dialCode + cleanValue.replace(/^0+/, "");
                        }
                      }
                      setRecipient({ ...recipient, phone: cleanValue })
                    }}
                    inputClass="inv-phone-input"
                    containerClass="inv-phone-container"
                    buttonClass="inv-phone-button"
                    enableSearch
                    searchPlaceholder="Search country..."
                    disableCountryGuess={true}
                  />
                </div>
              </div>
            </div>

            {/* ── Invoice Details ── */}
            <div className="inv-section">
              <div className="inv-section-title">
                <FaFileInvoiceDollar className="inv-section-icon" />
                Document Details
              </div>
              <div className="inv-form-grid">
                <div className="inv-form-group inv-full-width" style={{ gridColumn: "1 / -1" }}>
                  <label>Document Type</label>
                  <div style={{ display: "flex", gap: "20px", marginTop: "8px" }}>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        value="invoice"
                        checked={documentType === "invoice"}
                        onChange={() => setDocumentType("invoice")}
                        style={{ margin: 0 }}
                      />
                      Invoice
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: "8px", cursor: "pointer" }}>
                      <input
                        type="radio"
                        value="receipt"
                        checked={documentType === "receipt"}
                        onChange={() => setDocumentType("receipt")}
                        style={{ margin: 0 }}
                      />
                      Payment Receipt
                    </label>
                  </div>
                </div>
                <div className="inv-form-group">
                  <label>{documentType === "receipt" ? "Receipt Number" : "Invoice Number"}</label>
                  <input
                    type="text"
                    placeholder={`e.g. ${documentType === "receipt" ? "REC" : "INV"}-2026-001`}
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                  />
                </div>
                <div className="inv-form-group">
                  <label>{documentType === "receipt" ? "Receipt Date" : "Invoice Date"}</label>
                  <input
                    type="date"
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* ── Line Items Table ── */}
            <div className="inv-section">
              <div className="inv-section-title">
                <FaTable className="inv-section-icon" />
                Line Items
              </div>
              <div className="inv-table-wrapper">
                <table className="inv-table">
                  <thead>
                    <tr>
                      <th className="inv-th inv-th-sno">#</th>
                      <th className="inv-th inv-th-desc">Description</th>
                      <th className="inv-th inv-th-qty">QTY</th>
                      <th className="inv-th inv-th-price">Unit Price (₹)</th>
                      <th className="inv-th inv-th-amount">Amount (₹)</th>
                      <th className="inv-th inv-th-action"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, idx) => {
                      const amt = rowAmount(row);
                      return (
                        <tr
                          key={row.id}
                          className={`inv-table-row ${
                            animatingRowId === row.id ? "inv-row-enter" : ""
                          }`}
                        >
                          <td className="inv-td-sno">{idx + 1}</td>
                          <td>
                            <textarea
                              className="inv-input inv-input-desc"
                              placeholder="Service / Product description"
                              rows={2}
                              value={row.description}
                              onChange={(e) =>
                                updateRow(row.id, "description", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="inv-input inv-input-qty"
                              placeholder="0"
                              min="0"
                              value={row.qty}
                              onChange={(e) =>
                                updateRow(row.id, "qty", e.target.value)
                              }
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              className="inv-input inv-input-price"
                              placeholder="0.00"
                              min="0"
                              value={row.unitPrice}
                              onChange={(e) =>
                                updateRow(row.id, "unitPrice", e.target.value)
                              }
                            />
                          </td>
                          <td className="inv-td-amount">
                            {formatCurrency(amt)}
                          </td>
                          <td className="inv-td-action">
                            <button
                              className="inv-remove-btn"
                              onClick={() => removeRow(row.id)}
                              disabled={rows.length === 1}
                              title="Remove row"
                            >
                              <FaTrash />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <button className="inv-add-row-btn" onClick={addRow}>
                  <FaPlus /> Add Item
                </button>
              </div>
            </div>

            {/* ── Summary ── */}
            <div className="inv-section inv-summary-section">
              <div className="inv-summary-grid">
                {/* Discount Input */}
                <div className="inv-discount-card">
                  <label>Discount %</label>
                  <div className="inv-discount-input-wrap">
                    <input
                      type="number"
                      className="inv-discount-input"
                      placeholder="0"
                      min="0"
                      max="100"
                      value={discount}
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        if (rawValue === "") {
                          setDiscount("");
                          return;
                        }
                        let val = parseFloat(rawValue);
                        if (isNaN(val)) return;
                        if (val > 100) val = 100;
                        if (val < 0) val = 0;
                        setDiscount(val);
                      }}
                    />
                    <span className="inv-discount-suffix">%</span>
                  </div>
                </div>

                {/* Totals */}
                <div className="inv-totals-card">
                  <div className="inv-totals-row">
                    <span className="inv-totals-label">Subtotal</span>
                    <span className="inv-totals-value">
                      {formatCurrency(subtotal)}
                    </span>
                  </div>

                  {discountPercent > 0 && (
                    <div className="inv-totals-row inv-discount-row">
                      <span className="inv-totals-label">
                        Discount ({discountPercent}%)
                      </span>
                      <span className="inv-totals-value inv-discount-value">
                        - {formatCurrency(discountAmount)}
                      </span>
                    </div>
                  )}

                  <div className="inv-totals-divider" />

                  <div className="inv-totals-row inv-total-due-row">
                    <span className="inv-totals-label">{documentType === "receipt" ? "Bill Amount" : "Total Amount Due"}</span>
                    <span className="inv-totals-value">
                      {formatCurrency(totalDue)}
                    </span>
                  </div>
                  
                  {documentType === "receipt" && (
                    <>
                      <div className="inv-totals-divider" style={{ margin: "10px 0" }} />
                      <div className="inv-totals-row" style={{ alignItems: "center" }}>
                        <span className="inv-totals-label">Previous Payment</span>
                        <input 
                          type="number"
                          placeholder="0"
                          value={previousPaymentAmount}
                          onChange={(e) => setPreviousPaymentAmount(e.target.value)}
                          style={{ width: "120px", textAlign: "right", padding: "6px 8px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "14px" }}
                        />
                      </div>
                      <div className="inv-totals-row" style={{ alignItems: "center", marginTop: "10px" }}>
                        <span className="inv-totals-label">Current Payment</span>
                        <input 
                          type="number"
                          placeholder="0"
                          value={paymentAmount}
                          onChange={(e) => setPaymentAmount(e.target.value)}
                          style={{ width: "120px", textAlign: "right", padding: "6px 8px", borderRadius: "4px", border: "1px solid #ccc", fontSize: "14px" }}
                        />
                      </div>
                      <div className="inv-totals-row" style={{ alignItems: "center", marginTop: "10px" }}>
                        <span className="inv-totals-label">Balance Amount</span>
                        <span className="inv-totals-value" style={{ fontWeight: "bold" }}>
                          {formatCurrency(balanceAmount)}
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* ── Bank Details ── */}
            <div className="inv-section inv-bank-section">
              <div className="inv-section-title">
                <FaUniversity className="inv-section-icon" />
                Bank Details for Payment
              </div>
              <div className="inv-bank-grid">
                <div className="inv-bank-item">
                  <span className="inv-bank-label">Account Holder Name</span>
                  <span className="inv-bank-value">{BANK.holderName}</span>
                </div>
                <div className="inv-bank-item">
                  <span className="inv-bank-label">Account Number</span>
                  <span className="inv-bank-value">{BANK.accountNumber}</span>
                </div>
                <div className="inv-bank-item">
                  <span className="inv-bank-label">IFSC Code</span>
                  <span className="inv-bank-value">{BANK.ifsc}</span>
                </div>
                <div className="inv-bank-item">
                  <span className="inv-bank-label">Bank</span>
                  <span className="inv-bank-value">{BANK.bank}</span>
                </div>
                <div className="inv-bank-item">
                  <span className="inv-bank-label">Branch</span>
                  <span className="inv-bank-value">{BANK.branch}</span>
                </div>
              </div>
            </div>
          </div>

          {/* ══════════ ACTION BUTTONS ══════════ */}
          <div className="inv-actions">
            {!isGeneratingPdf ? (
              <button
                className="inv-generate-btn"
                onClick={handleGeneratePdf}
                disabled={!invoiceNumber}
              >
                <FaFileInvoiceDollar />
                Generate & Download {documentType === "receipt" ? "Receipt" : "Invoice"}
              </button>
            ) : (
              <button className="inv-generate-btn inv-generating" disabled>
                <i className="fa-solid fa-spinner fa-spin"></i>
                Generating {documentType === "receipt" ? "Receipt" : "Invoice"}...
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default InvoiceGenerator;
